class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => this.classList.add('animate', 'active'));
    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });
    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState = {}) {
    this.productId = parsedState.id;

    const drawerSectionHTML = parsedState.sections?.['cart-drawer'];
    if (drawerSectionHTML) {
      const sourceWrapper = this.getSectionDOM(drawerSectionHTML, 'cart-drawer');
      const sourceDrawer = this.getSectionDOM(drawerSectionHTML, '#CartDrawer');
      const isEmpty = sourceWrapper
        ? sourceWrapper.classList.contains('is-empty')
        : Boolean(sourceDrawer?.querySelector('.drawer__inner-empty')) && !sourceDrawer?.querySelector('.cart-item');
      this.classList.toggle('is-empty', isEmpty);
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHTML = parsedState.sections?.[section.id];
      if (!sectionElement || !sectionHTML) return;

      const replacementHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
      if (replacementHTML === null) return;
      sectionElement.innerHTML = replacementHTML;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      if (parsedState.open === false) return;

      if (this.classList.contains('active')) {
        document.body.classList.add('overflow-hidden');
        const focusContainer = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (focusContainer && focusElement) trapFocus(focusContainer, focusElement);
      } else {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);

(() => {
  const STYLE_ID = 'bhakti-cart-mrp-offer-style';
  const productCache = new Map();

  const addStyle = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .bhakti-cart-mrp-offer {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 0.55rem;
        margin-top: 0.35rem;
        font-size: 1.2rem;
        line-height: 1.25;
      }
      .bhakti-cart-mrp {
        color: rgba(var(--color-foreground), 0.62);
        white-space: nowrap;
      }
      .bhakti-cart-mrp s {
        text-decoration-thickness: 1.5px;
        text-decoration-color: currentColor;
      }
      .bhakti-cart-off {
        color: #167847;
        font-weight: 700;
        white-space: nowrap;
      }
    `;
    document.head.appendChild(style);
  };

  const formatINR = (cents) => {
    const value = Number(cents) / 100;
    return `Rs. ${value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getProduct = (href) => {
    const url = new URL(href, window.location.origin);
    const endpoint = `${url.origin}${url.pathname}.js`;
    if (!productCache.has(endpoint)) {
      productCache.set(
        endpoint,
        fetch(endpoint, { credentials: 'same-origin' }).then((response) => {
          if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
          return response.json();
        })
      );
    }
    return productCache.get(endpoint);
  };

  const enhanceRow = async (row) => {
    if (row.dataset.bhaktiMrpLoading === 'true') return;
    if (row.matches('.bhakti-cart-auto-gift') || row.querySelector('.bhakti-cart-auto-gift')) return;
    if (/\bFREE GIFT\b/i.test(row.textContent || '')) return;

    const nameLink = row.querySelector('.cart-item__name[href]');
    const variantElement = row.querySelector('[data-quantity-variant-id]');
    const variantId = variantElement?.dataset?.quantityVariantId;
    const details = row.querySelector('.cart-item__details');
    if (!nameLink || !variantId || !details) return;

    const quantity = Math.max(1, Number(variantElement.value || variantElement.getAttribute('value') || 1));
    const quantityKey = String(quantity);
    if (row.dataset.bhaktiMrpReady === 'true' && row.dataset.bhaktiMrpQty === quantityKey) return;

    row.dataset.bhaktiMrpLoading = 'true';

    try {
      const product = await getProduct(nameLink.href);
      const variant = product?.variants?.find((entry) => String(entry.id) === String(variantId));
      const compareAt = Number(variant?.compare_at_price || 0);
      const price = Number(variant?.price || 0);

      if (!(compareAt > price && price > 0)) {
        row.dataset.bhaktiMrpReady = 'true';
        row.dataset.bhaktiMrpQty = quantityKey;
        return;
      }

      const totalCompareAt = compareAt * quantity;
      const discountPercent = Math.round(((compareAt - price) / compareAt) * 100);
      row.querySelector('.bhakti-cart-mrp-offer')?.remove();

      const offer = document.createElement('div');
      offer.className = 'bhakti-cart-mrp-offer';
      offer.setAttribute('aria-label', `MRP ${formatINR(totalCompareAt)}, ${discountPercent}% off`);

      const mrp = document.createElement('span');
      mrp.className = 'bhakti-cart-mrp';
      mrp.append(document.createTextNode('MRP '));
      const struckPrice = document.createElement('s');
      struckPrice.textContent = formatINR(totalCompareAt);
      mrp.appendChild(struckPrice);

      const off = document.createElement('span');
      off.className = 'bhakti-cart-off';
      off.textContent = `${discountPercent}% OFF`;

      offer.append(mrp, off);

      const directChildren = Array.from(details.children).reverse();
      const priceAnchor = directChildren.find((element) => /Rs\.|₹|\bINR\b/i.test(element.textContent || ''));
      if (priceAnchor) {
        priceAnchor.insertAdjacentElement('afterend', offer);
      } else {
        details.appendChild(offer);
      }

      row.dataset.bhaktiMrpReady = 'true';
      row.dataset.bhaktiMrpQty = quantityKey;
    } catch (error) {
      console.warn('Cart MRP display skipped:', error);
    } finally {
      delete row.dataset.bhaktiMrpLoading;
    }
  };

  const enhanceCart = (drawer) => {
    addStyle();
    drawer.querySelectorAll('.cart-item').forEach((row) => enhanceRow(row));
  };

  const init = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    enhanceCart(drawer);

    drawer.addEventListener('change', (event) => {
      const input = event.target.closest?.('[data-quantity-variant-id]');
      const row = input?.closest?.('.cart-item');
      if (!row) return;
      delete row.dataset.bhaktiMrpReady;
      delete row.dataset.bhaktiMrpQty;
      enhanceRow(row);
    });

    const observer = new MutationObserver(() => enhanceCart(drawer));
    observer.observe(drawer, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

(() => {
  const TWO_MALA_VARIANTS = new Set([
    '48574448337137',
    '48593611161841',
    '48593612407025',
    '48593614274801',
    '48593615454449',
    '48593616109809',
  ]);

  const updateMalaQuantityLabels = (drawer) => {
    drawer.querySelectorAll('.cart-item').forEach((row) => {
      if (row.matches('.bhakti-cart-auto-gift') || row.querySelector('.bhakti-cart-auto-gift')) return;
      if (/\bFREE GIFT\b/i.test(row.textContent || '')) return;

      const quantityInput = row.querySelector('[data-quantity-variant-id]');
      const variantId = quantityInput?.dataset?.quantityVariantId;
      if (!quantityInput || !variantId) return;

      const quantity = Math.max(1, Number(quantityInput.value || quantityInput.getAttribute('value') || 1));
      const malasPerUnit = TWO_MALA_VARIANTS.has(String(variantId)) ? 2 : 1;
      const totalMalas = quantity * malasPerUnit;
      const desiredLabel = `${totalMalas} Mala${totalMalas === 1 ? '' : 's'}`;

      row.querySelectorAll('.cart-item__details .product-option dd').forEach((label) => {
        const currentLabel = (label.textContent || '').trim();
        if (!/^\d+\s+Malas?$/i.test(currentLabel)) return;
        if (currentLabel === desiredLabel) return;
        label.textContent = desiredLabel;
      });
    });
  };

  const initMalaQuantityLabels = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    updateMalaQuantityLabels(drawer);

    drawer.addEventListener('change', (event) => {
      if (!event.target.closest?.('[data-quantity-variant-id]')) return;
      requestAnimationFrame(() => updateMalaQuantityLabels(drawer));
    });

    const observer = new MutationObserver(() => updateMalaQuantityLabels(drawer));
    observer.observe(drawer, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMalaQuantityLabels, { once: true });
  } else {
    initMalaQuantityLabels();
  }
})();

// Pre-publish hardening: cart drawer uses Drawer-quantity-* IDs while the base
// CartItems recovery method looks only for Quantity-* IDs. Patch both cart
// element classes so invalid/min/max/step recovery can never throw on a missing input.
(() => {
  const hardenQuantityReset = () => {
    const constructors = [customElements.get('cart-items'), customElements.get('cart-drawer-items')].filter(Boolean);
    constructors.forEach((Constructor) => {
      Constructor.prototype.resetQuantityInput = function resetQuantityInput(id) {
        const input = this.querySelector(`#Quantity-${id}`) || this.querySelector(`#Drawer-quantity-${id}`);
        if (!input) return;
        input.value = input.getAttribute('value');
        this.isEnterPressed = false;
      };
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hardenQuantityReset, { once: true });
  } else {
    hardenQuantityReset();
  }
})();
